import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, KeyRound, User } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/authService';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';

/** Alinhado a `AuthController.validateChangePassword` na API */
const NEW_PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;

function validatePasswordForm(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): { current?: string; new?: string; confirm?: string } | null {
  const errors: { current?: string; new?: string; confirm?: string } = {};
  if (!currentPassword) errors.current = 'Informe a senha atual.';
  if (newPassword.length < 6) {
    errors.new = 'Nova senha deve ter pelo menos 6 caracteres.';
  } else if (!NEW_PASSWORD_REGEX.test(newPassword)) {
    errors.new = 'Nova senha deve conter: 1 minúscula, 1 maiúscula e 1 número.';
  }
  if (newPassword !== confirmPassword) errors.confirm = 'As senhas não coincidem.';
  return Object.keys(errors).length ? errors : null;
}

export const HeaderUserMenu: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const logoutStore = useAuthStore((s) => s.logout);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    current?: string;
    new?: string;
    confirm?: string;
  }>({});
  const [saving, setSaving] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (e: MouseEvent | PointerEvent) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target as Node)) closeMenu();
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen, closeMenu]);

  const openChangePassword = () => {
    closeMenu();
    setFieldErrors({});
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPwdModalOpen(true);
  };

  const closePasswordModal = () => {
    setPwdModalOpen(false);
    setFieldErrors({});
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validatePasswordForm(currentPassword, newPassword, confirmPassword);
    if (errs) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    setSaving(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success('Senha alterada. Faça login novamente com a nova senha.');
      closePasswordModal();
      try {
        await authService.logout(refreshToken ?? undefined);
      } catch {
        /* refresh já pode estar inválido */
      }
      logoutStore();
      navigate('/login', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Não foi possível alterar a senha.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const displayName = user?.name || 'Usuário';
  const displayRole = user?.role || 'N/A';

  return (
    <div ref={containerRef} className="relative flex items-center gap-x-2">
      <button
        type="button"
        className={cn(
          'flex items-center gap-x-2 rounded-input px-1 py-1 text-left transition-colors',
          'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        )}
        aria-haspopup="menu"
        aria-controls={menuOpen ? 'header-user-menu-dropdown' : undefined}
        aria-expanded={menuOpen}
        aria-label={`Menu do usuário: ${displayName}`}
        onClick={() => setMenuOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setMenuOpen((o) => !o);
          }
        }}
      >
        <div className="w-9 h-9 flex shrink-0 items-center justify-center rounded-input bg-primary shadow-soft-sm">
          <User className="h-4 w-4 text-primary-foreground" aria-hidden />
        </div>
        <div className="hidden lg:block min-w-0">
          <p className="font-body text-body-sm font-medium text-foreground truncate">{displayName}</p>
          <p className="font-body text-body-xs text-muted-foreground truncate">{displayRole}</p>
        </div>
        <ChevronDown
          className={cn(
            'hidden lg:block h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            menuOpen && 'rotate-180'
          )}
          aria-hidden
        />
      </button>

      {menuOpen ? (
        <div
          id="header-user-menu-dropdown"
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[200px] rounded-input border border-border bg-card py-1 shadow-soft-md"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 font-body text-body-sm text-foreground hover:bg-muted"
            onClick={openChangePassword}
          >
            <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            Alterar senha
          </button>
        </div>
      ) : null}

      <Modal isOpen={pwdModalOpen} onClose={closePasswordModal} title="Alterar senha" size="sm">
        <form onSubmit={handleSubmitPassword} className="space-y-4">
          <Input
            label="Senha atual"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            error={fieldErrors.current}
            autoComplete="current-password"
            required
          />
          <Input
            label="Nova senha"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={fieldErrors.new}
            autoComplete="new-password"
            required
            helperText="Mínimo 6 caracteres, com letra minúscula, maiúscula e número."
          />
          <Input
            label="Confirmar nova senha"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={fieldErrors.confirm}
            autoComplete="new-password"
            required
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={closePasswordModal} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving} disabled={!currentPassword || !newPassword || !confirmPassword}>
              Salvar
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
